import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const API_URL = 'correspondencias/';

// Interfaces para Correspondencia
export interface Correspondencia {
    id: number;
    codigo?: string | null;
    cliente?: number | null;
    afiliacion?: number | null;
    afiliacion_empresa?: string | null;
    afiliacion_nit?: string | null;
    servicio?: number | null;
    servicio_nombre?: string | null;
    cita_correspondencia?: number | null;
    cita_id?: number | null;
    cita_codigo?: string | null;
    destinatario_nombre?: string | null;
    destinatario_telefono?: string | null;
    destinatario_email?: string | null;
    destinatario_ci_nit?: string | null;
    cliente_nombre?: string | null;
    cliente_email?: string | null;
    estado: 'recibido' | 'notificado' | 'entregado' | 'cancelado';
    descripcion: string | null;
    imagen?: string | null;
    imagen_entrega?: string | null;
    pin_seguridad?: string | null;
    token_verificacion?: string | null;
    fecha_recepcion: string;
    fecha_entrega: string | null;
    
    remitente_nombre: string;
    remitente_apellido: string;
    remitente_telefono: string;
    remitente_ci_nit: string;
    
    receptor_nombre: string | null;
    receptor_apellido: string | null;
    receptor_telefono: string | null;
    receptor_ci_nit: string | null;
    
    usuario_recibe: number | null;
    usuario_recibe_nombre?: string | null;
    usuario_entrega: number | null;
    usuario_entrega_nombre?: string | null;
    fecha_creacion: string;
}

export interface CorrespondenciaCreate {
    cliente?: number | null;
    afiliacion?: number | null;
    servicio?: number | null;
    cita_correspondencia?: number | null;
    destinatario_nombre?: string | null;
    destinatario_telefono?: string | null;
    destinatario_email?: string | null;
    destinatario_ci_nit?: string | null;
    estado?: 'recibido' | 'notificado' | 'entregado' | 'cancelado';
    descripcion?: string | null;
    imagen?: File | null;
    
    remitente_nombre?: string;
    remitente_apellido?: string;
    remitente_telefono?: string;
    remitente_ci_nit?: string;
    
    receptor_nombre?: string | null;
    receptor_apellido?: string | null;
    receptor_telefono?: string | null;
    receptor_ci_nit?: string | null;
}

export interface ClienteElegibleCorrespondencia {
    id: number;
    nombre: string;
    email: string;
    telefono?: string;
    ci_nit?: string;
    rol?: string;
    es_domiciliado: boolean;
    domiciliacion?: {
        afiliacion_id: number;
        empresa_nombre: string;
        nit?: string;
        representante_legal?: string;
        estado: string;
        tipo_contrato: string;
    } | null;
    tiene_citas_correspondencia: boolean;
    citas_correspondencia: Array<{
        cita_correspondencia_id: number;
        cita_id: number;
        cita_codigo: string;
        servicio_nombre: string;
    }>;
}

export interface CorrespondenciaUpdate {
    estado: 'recibido' | 'notificado' | 'entregado' | 'cancelado';
    descripcion?: string | null;
    imagen?: File | null;
    imagen_entrega?: File | null;
    
    // Datos del receptor
    receptor_nombre?: string | null;
    receptor_apellido?: string | null;
    receptor_telefono?: string | null;
    receptor_ci_nit?: string | null;
}

export interface ResumenCorrespondencia {
    total_recibidas: number;
    total_entregadas: number;
    total_pendientes: number;
    ultima_semana: number;
}

// Operaciones CRUD básicas
export const fetchCorrespondencias = async (): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL);
        return response.data;
    });
};

export const createCorrespondencia = async (data: CorrespondenciaCreate): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        let payload: any = data;
        let headers: Record<string, string> = {};

        // Si tiene imagen (File), enviamos como FormData
        if (data.imagen instanceof File) {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    if (key === 'imagen' && value instanceof File) {
                        formData.append('imagen', value);
                    } else {
                        formData.append(key, String(value));
                    }
                }
            });
            payload = formData;
            headers['Content-Type'] = 'multipart/form-data';
        }

        const response = await axiosInstance.post(API_URL, payload, { headers });
        return response.data;
    });
};

export const updateCorrespondencia = async (id: number, data: CorrespondenciaUpdate): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        let payload: any = data;
        let headers: Record<string, string> = {};

        if (data.imagen instanceof File || data.imagen_entrega instanceof File) {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    if ((key === 'imagen' || key === 'imagen_entrega') && value instanceof File) {
                        formData.append(key, value);
                    } else {
                        formData.append(key, String(value));
                    }
                }
            });
            payload = formData;
            headers['Content-Type'] = 'multipart/form-data';
        }

        const response = await axiosInstance.patch(`${API_URL}${id}/`, payload, { headers });
        return response.data;
    });
};

export const deleteCorrespondencia = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`${API_URL}${id}/`);
    });
};

export const getCorrespondenciaById = async (id: number): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        return response.data;
    });
};

// Filtros y acciones personalizadas
export const fetchCorrespondenciasFiltradas = async (filtros: {
    cita?: number;
    cliente?: number;
    afiliacion?: number;
    estado?: 'recibido' | 'notificado' | 'entregado' | 'cancelado';
    codigo?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
}): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL, { params: filtros });
        return response.data;
    });
};

export const fetchCorrespondenciasPorCita = async (
    cita_id: number, 
    cliente_id?: number, 
    es_cliente_asociado: boolean = false
): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const params: any = { 
            cita: cita_id,
            cliente_id,
            es_cliente_asociado
        };
        const response = await axiosInstance.get(API_URL, { params });
        return response.data;
    });
};

export const fetchCorrespondenciasPorEstado = async (estado: string): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL, { params: { estado } });
        return response.data;
    });
};

export const fetchResumenCorrespondencias = async (): Promise<ResumenCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}resumen/`);
        return response.data;
    });
};

// Acciones específicas de estado
export const marcarComoNotificado = async (id: number): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.patch(`${API_URL}${id}/marcar_notificado/`);
        return response.data;
    });
};

export const notificarLlegadaCorrespondencia = async (
    id: number,
    data: { imagen?: File | null; observaciones?: string }
): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        let payload: any = {};
        let headers: Record<string, string> = {};

        if (data.imagen instanceof File) {
            const formData = new FormData();
            formData.append('imagen', data.imagen);
            if (data.observaciones) formData.append('observaciones', data.observaciones);
            payload = formData;
            headers['Content-Type'] = 'multipart/form-data';
        } else {
            if (data.observaciones) payload.observaciones = data.observaciones;
        }

        const response = await axiosInstance.post(
            `${API_URL}${id}/notificar_llegada/`,
            payload,
            { headers }
        );
        return response.data;
    });
};

export const marcarComoEntregado = async (
    id: number, 
    entregaData: {
        receptor_nombre: string;
        receptor_apellido: string;
        receptor_telefono: string;
        receptor_ci_nit: string;
        pin_seguridad?: string;
        token_verificacion?: string;
        imagen_entrega?: File | null;
    }
): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        let payload: any = { estado: 'entregado', ...entregaData };
        let headers: Record<string, string> = {};

        if (entregaData.imagen_entrega instanceof File) {
            const formData = new FormData();
            formData.append('estado', 'entregado');
            formData.append('receptor_nombre', entregaData.receptor_nombre);
            formData.append('receptor_apellido', entregaData.receptor_apellido || '');
            formData.append('receptor_telefono', entregaData.receptor_telefono || '');
            formData.append('receptor_ci_nit', entregaData.receptor_ci_nit);
            if (entregaData.pin_seguridad) {
                formData.append('pin_seguridad', entregaData.pin_seguridad);
            }
            if (entregaData.token_verificacion) {
                formData.append('token_verificacion', entregaData.token_verificacion);
            }
            formData.append('imagen_entrega', entregaData.imagen_entrega);
            payload = formData;
            headers['Content-Type'] = 'multipart/form-data';
        }

        const response = await axiosInstance.post(
            `${API_URL}${id}/marcar_entregado/`,
            payload,
            { headers }
        );
        return response.data;
    });
};

export const validarQrCorrespondencia = async (token: string): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}validar-qr/`, { token });
        return response.data;
    });
};

export const cancelarCorrespondencia = async (id: number): Promise<Correspondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.patch(`${API_URL}${id}/cancelar/`);
        return response.data;
    });
};

// Búsqueda por datos del remitente/receptor
export const buscarPorRemitente = async (ci_nit: string): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}buscar_remitente/?ci_nit=${ci_nit}`);
        return response.data;
    });
};

export const buscarPorReceptor = async (ci_nit: string): Promise<Correspondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}buscar_receptor/?ci_nit=${ci_nit}`);
        return response.data;
    });
};

// Estadísticas y reportes
export const fetchEstadisticasMensuales = async (mes?: number, año?: number): Promise<any> => {
    return handleRequestWithAuth(async () => {
        const params: any = {};
        if (mes) params.mes = mes;
        if (año) params.año = año;
        
        const response = await axiosInstance.get(`${API_URL}estadisticas_mensuales/`, { params });
        return response.data;
    });
};

export const generarReporteCorrespondencias = async (filtros: {
    fecha_desde?: string;
    fecha_hasta?: string;
    estado?: string;
    usuario?: number;
}): Promise<Blob> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}generar_reporte/`, {
            params: filtros,
            responseType: 'blob'
        });
        return response.data;
    });
};

export const fetchSeguimientoPublico = async (tokenOcodigo: string): Promise<Correspondencia> => {
    const cleanToken = (tokenOcodigo || '').trim();
    const response = await axiosInstance.get(`${API_URL}seguimiento-publico/`, {
        params: { token: cleanToken }
    });
    return response.data;
};

export const fetchClientesElegiblesCorrespondencia = async (): Promise<ClienteElegibleCorrespondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}clientes_elegibles/`);
        const list = response.data?.results || response.data || [];
        return Array.isArray(list) ? list : [];
    });
};

export interface MiAccesoCorrespondencia {
    tiene_acceso: boolean;
    is_staff_or_admin: boolean;
    es_domiciliado: boolean;
    empresa_nombre?: string | null;
    tiene_servicio_correspondencia: boolean;
    servicios_correspondencia?: Array<{
        cita_id: number;
        cita_codigo: string;
        servicio_nombre: string;
    }>;
}

export const fetchMiAccesoCorrespondencia = async (): Promise<MiAccesoCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}mi_acceso/`);
        return response.data;
    });
};
